import React, { useEffect, useState } from "react";
import { Pen, PenSmall } from "../../../../assets/icons/PenIcon";
import { useUserStore } from "../../../../state";
import { BankAccountData, BankData } from "../../../../types";
import { CreateModal } from "../../../common/CreateModal";
import BankAccountModal from "../../bank/bankAccountModal";
import CreateBank from "../../bank/createBank";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "../../../../state/useCompanyStore";
import PlusIcon from "../../../../assets/icons/PlusIcon";
import { showToast } from "../../../../utils/toastUtils";

const BankDetails = () => {
  // const user = useUserStore((state) => state.user);
  // const setUser = useUserStore((state) => state.setUser);
  const { companyBankAccounts, setCompanyBankAccounts, company, setCompany } =
    useCompanyStore();

  const { t } = useTranslation("invoices");
  if (!company) {
    return null;
  }
  const bankAccounts = company.bankAccounts ? company.bankAccounts.flat() : [];
  const [showBankModal, setShowBankModal] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [showCreateBank, setShowCreateBank] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toggleRender, setToggleRender] = useState(false);
  const createBankAccount = useCompanyStore((state) => state.createBankAccount);
  const activeBankData = bankAccounts.filter(
    (bankAccount: BankData) => bankAccount.isActive,
  );

  const toggleBankModal = () => {
    setShowBankModal(!showBankModal);
  };

  const handleAddBankAccount = (newBankAccount: BankData) => {
    const updatedUser = {
      ...company,
      bankAccounts: company?.bankAccounts
        ? [...company?.bankAccounts, newBankAccount]
        : [newBankAccount],
    };
    setCompany(updatedUser);
    setSelectedAccounts([...selectedAccounts, newBankAccount.accountNumber]);
    setToggleRender(!toggleRender);
  };
  const handleCreateBankAccount = async (formData: BankData) => {
    setIsLoading(true);

    try {
      await createBankAccount(formData);
      const updatedUser = company;

      if (
        updatedUser &&
        Array.isArray(updatedUser.bankAccounts) &&
        updatedUser.bankAccounts.length > 0
      ) {
        const lastBankAccount =
          updatedUser.bankAccounts[updatedUser.bankAccounts.length - 1];

        const updatedBankAccount = {
          ...lastBankAccount,
          isActive:
            lastBankAccount.isActive !== undefined
              ? lastBankAccount.isActive
              : true, // Set to true if undefined
        };

        handleAddBankAccount(updatedBankAccount);

        setCompany((prevUser: { bankAccounts: BankAccountData[] }) => ({
          ...prevUser,
          bankAccounts: [...prevUser.bankAccounts, updatedBankAccount],
        }));

        setCompanyBankAccounts(updatedUser.bankAccounts);

        setToggleRender(!toggleRender);
        setShowCreateBank(false);

        showToast("Bank account created successfully", "success");
      } else {
        console.error("No bank accounts found in the updated user data");
      }
    } catch (error) {
      console.error("Error creating bank account:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Trigger a re-render whenever toggleRender changes
  }, [toggleRender]);

  const displayedBankData =
    selectedAccounts.length > 0
      ? bankAccounts.filter((bank: { accountNumber: string }) =>
          selectedAccounts.includes(bank.accountNumber),
        )
      : activeBankData;

  return (
    <>
      {/* Desktop */}
      <div className="hidden lg:block md:block w-[400px]  mt-[5em]">
        <div className="flex justify-between items-center mb-1">
          <p className="text-[16px] font-medium leading-5  whitespace-nowrap">
            Bank details
          </p>
          <button
            onClick={toggleBankModal}
            className="font-medium  text-[16px] flex items-center gap-1 p-2 rounded bg-[#E0EEFE] text-[#0469C5] "
          >
            <span className="">
              <PlusIcon />{" "}
            </span>{" "}
            {bankAccounts.length > 0 ? "Change account" : "Add account"}
          </button>
        </div>

        <div className=" cursor-pointer mt-2 ">
          <div>
            {displayedBankData.length === 0 && (
              <div className="py-2 px-3 my-2 bg-[#F9FAFB] italic">
                <p className="leading-5 mb-1 text-[#]">Account Name</p>
                <div className="flex flex-col items-start  gap-1 mt-1 leading-5 text-gray-500">
                  <p>Account Number</p>
                  <div className=" rounded-full bg-gray-300" />
                  <p>Bank Name</p>
                </div>
              </div>
            )}
            {displayedBankData.map((bank: BankData, index: number) => (
              <div key={index} className="py-2 px-3 my-2 bg-[#F9FAFB]">
                <p className="font-semibold text-[14px] leading-5 mb-1">
                  {bank.accountName}
                </p>
                <div className="flex flex-col items-start  font-medium text-[14px] leading-5 text-gray-500">
                  <p>{bank.accountNumber}</p>
                  <div className=" rounded-full bg-gray-300" />
                  <p>{bank.bankName}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Mobile */}
      <div className="w-full pt-5  border-gray-200 md:hidden">
        <p className="text-[14px] font-medium leading-5 mb-1">Bank details</p>
        {displayedBankData.map((bank: BankData, index: number) => (
          <div key={index} className="py-2 px-3 my-2 bg-[#F9FAFB]">
            <p className="font-semibold text-[14px] leading-5 mb-1">
              {bank.accountName}
            </p>
            <div className="flex items-center gap-1 font-medium text-[14px] leading-5 text-gray-500">
              <p>{bank.accountNumber}</p>
              <div className="w-1 h-1 rounded-full bg-gray-300" />
              <p>{bank.bankName}</p>
            </div>
          </div>
        ))}
        <button
          onClick={toggleBankModal}
          className="font-medium py-[10px] text-[16px] leading-6 text-[#0469C5] w-full text-center"
        >
          {bankAccounts.length > 0 ? "Change account" : "Add bank account"}
        </button>
      </div>

      {showBankModal && !showCreateBank && (
        <CreateModal>
          <BankAccountModal
            activeBankData={bankAccounts}
            selectedAccounts={selectedAccounts}
            setSelectedAccounts={setSelectedAccounts}
            toggleModal={toggleBankModal}
            onShowCreateBank={() => setShowCreateBank(true)}
          />
        </CreateModal>
      )}

      {showCreateBank && (
        <CreateModal>
          <CreateBank
            onCreate={handleCreateBankAccount}
            showSideModal={showCreateBank}
            setShowSideModal={setShowCreateBank}
          />
        </CreateModal>
      )}
    </>
  );
};

export default BankDetails;
